import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/lib/constants";

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7] text-[#22223B]">
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-black/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="EdgeTech Global"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive
                      ? "text-[#0F4C5C] font-medium"
                      : "text-[#22223B]/70 hover:text-[#0F4C5C]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact?interest=testfit_demo"
              data-testid="nav-cta-schedule"
              className="btn-primary text-sm"
            >
              Schedule a Demo <ArrowUpRight size={16} strokeWidth={1.6} />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-[#0F4C5C]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {open && (
          <div
            data-testid="nav-mobile-panel"
            className="lg:hidden bg-[#FDFBF7] border-t border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-base ${
                      isActive
                        ? "text-[#0F4C5C] font-medium"
                        : "text-[#22223B]/80"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/contact?interest=testfit_demo"
                data-testid="nav-mobile-cta"
                className="btn-primary text-sm mt-2 justify-center"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0F4C5C] text-[#FDFBF7] mt-24 grain overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <img
            src={LOGO_URL}
            alt="EdgeTech Global"
            className="h-10 w-auto brightness-0 invert opacity-90 mb-6"
          />
          <p className="font-serif-display text-2xl leading-snug max-w-md">
            Decision intelligence for education. Better decisions build better
            schools.
          </p>
          <p className="mt-6 text-sm text-[#FDFBF7]/70 max-w-md">
            EdgeTech Global equips schools and educational leaders with the
            diagnostic tools, intelligent systems, curriculum and leadership
            solutions they need to turn information into clear, strategic
            action.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="overline text-[#FDFBF7]/60 mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-[#FDFBF7]/90 hover:text-[#E3B23C] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/contact"
                className="text-[#FDFBF7]/90 hover:text-[#E3B23C] transition-colors"
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline text-[#FDFBF7]/60 mb-4">Family of Brands</div>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                href="https://testfit.org"
                target="_blank"
                rel="noreferrer"
                className="text-[#FDFBF7]/90 hover:text-[#E3B23C] transition-colors inline-flex items-center gap-1"
              >
                TestFit® <ArrowUpRight size={14} />
              </a>{" "}
              <span className="text-[#FDFBF7]/50">— The flagship diagnostic</span>
            </li>
            <li>
              <a
                href="https://drcrystalgifford.com"
                target="_blank"
                rel="noreferrer"
                className="text-[#FDFBF7]/90 hover:text-[#E3B23C] transition-colors inline-flex items-center gap-1"
              >
                Dr. Crystal Gifford <ArrowUpRight size={14} />
              </a>{" "}
              <span className="text-[#FDFBF7]/50">— Speaking &amp; keynotes</span>
            </li>
          </ul>

          <div className="overline text-[#FDFBF7]/60 mt-8 mb-3">Contact</div>
          <p className="text-sm text-[#FDFBF7]/80 leading-relaxed">
            Miami, FL
            <br />
            +1 740&nbsp;302&nbsp;3222
            <br />
            <a
              href="mailto:admin@crystalgifford.com"
              className="hover:text-[#E3B23C] transition-colors"
            >
              admin@crystalgifford.com
            </a>
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#FDFBF7]/60">
          <p>© {new Date().getFullYear()} EdgeTech Global. All rights reserved.</p>
          <p className="font-serif-display italic text-sm text-[#E3B23C]/90 tracking-wide">
            Live Adventurously
          </p>
          <p className="tracking-widest uppercase text-[#FDFBF7]/50">
            Diagnose · Decide · Design · Deliver
          </p>
        </div>
      </div>
    </footer>
  );
}
